import React, { useState, useEffect } from 'react';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { useNavigate } from 'react-router-dom';
import { 
  Eye, MousePointerClick, Calendar, DollarSign, 
  Play, Pause, Trash2, Copy, RefreshCw, ArrowLeft 
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MyCampaignsPage = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchCampaigns();
  }, [filterStatus]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const url = filterStatus === 'all' 
        ? `${API_URL}/api/ads/user/campaigns/my-campaigns`
        : `${API_URL}/api/ads/user/campaigns/my-campaigns?status=${filterStatus}`;
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (campaignId) => {
    try {
      const response = await fetch(`${API_URL}/api/ads/user/campaigns/${campaignId}/pause`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Campaign paused');
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to pause');
      }
    } catch (error) {
      toast.error('Failed to pause campaign');
    }
  };

  const handleDelete = async (campaignId) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;

    try {
      const response = await fetch(`${API_URL}/api/ads/user/campaigns/${campaignId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Campaign deleted');
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      running: 'bg-green-100 text-green-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800',
      paused: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredCampaigns = filterStatus === 'all' 
    ? campaigns 
    : campaigns.filter(c => c.status === filterStatus);

  return (
    <FocusedLayout>
      <div className="space-y-6 p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">My Campaigns</h1>
              <p className="text-muted-foreground">Manage your advertising campaigns</p>
            </div>
          </div>
          <Button onClick={() => navigate('/discover/mini-apps/matrise-ads/create')}>
            Create Campaign
          </Button>
        </div>

        {/* Filter Tabs */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-2 flex-wrap">
              {['all', 'pending_approval', 'running', 'expired', 'rejected'].map((status) => (
                <Button
                  key={status}
                  variant={filterStatus === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus(status)}
                >
                  {status.replace('_', ' ').charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                  {status === 'all' && ` (${campaigns.length})`}
                  {status !== 'all' && ` (${campaigns.filter(c => c.status === status).length})`}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Campaigns List */}
        <div className="space-y-4">
          {loading ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Loading campaigns...</p>
              </CardContent>
            </Card>
          ) : filteredCampaigns.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <p className="text-muted-foreground mb-4">
                  {filterStatus === 'all' ? 'No campaigns yet' : `No ${filterStatus.replace('_', ' ')} campaigns`}
                </p>
                <Button onClick={() => navigate('/discover/mini-apps/matrise-ads/create')}>
                  Create Your First Campaign
                </Button>
              </CardContent>
            </Card>
          ) : (
            filteredCampaigns.map((campaign) => (
              <Card key={campaign.id}>
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Image */}
                    {campaign.image_url && (
                      <div className="w-full md:w-48 h-32 flex-shrink-0">
                        <img 
                          src={campaign.image_url} 
                          alt={campaign.headline}
                          className="w-full h-full object-cover rounded"
                        />
                      </div>
                    )}

                    {/* Details */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-xl font-bold">{campaign.campaign_name}</h3>
                          <p className="text-muted-foreground">{campaign.business_name}</p>
                        </div>
                        <Badge className={getStatusColor(campaign.status)}>
                          {campaign.status.replace('_', ' ')}
                        </Badge>
                      </div>

                      <div className="mb-4">
                        <p className="font-semibold mb-1">{campaign.headline}</p>
                        <p className="text-sm text-muted-foreground">{campaign.description}</p>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <div className="text-xs text-muted-foreground">Placement</div>
                          <div className="font-semibold capitalize">{campaign.placement}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Impressions</div>
                          <div className="font-semibold flex items-center gap-1">
                            <Eye className="h-4 w-4" />
                            {campaign.impressions || 0}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Clicks</div>
                          <div className="font-semibold flex items-center gap-1">
                            <MousePointerClick className="h-4 w-4" />
                            {campaign.clicks || 0}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">CTR</div>
                          <div className="font-semibold">{campaign.ctr?.toFixed(2) || 0}%</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <div className="text-xs text-muted-foreground">Total Spend</div>
                          <div className="font-semibold text-purple-600">
                            {campaign.total_cost.toLocaleString()} MTR
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Duration</div>
                          <div className="font-semibold">{campaign.duration_days} days</div>
                        </div>
                        {campaign.remaining_days !== undefined && (
                          <div>
                            <div className="text-xs text-muted-foreground">Remaining</div>
                            <div className="font-semibold">{campaign.remaining_days} days</div>
                          </div>
                        )}
                        {campaign.start_date && (
                          <div>
                            <div className="text-xs text-muted-foreground">Started</div>
                            <div className="font-semibold text-sm">
                              {new Date(campaign.start_date).toLocaleDateString()}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Rejection Reason */}
                      {campaign.status === 'rejected' && campaign.rejection_reason && (
                        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded p-3 mb-4">
                          <p className="text-sm text-red-800 dark:text-red-300">
                            <strong>Rejection Reason:</strong> {campaign.rejection_reason}
                          </p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2 flex-wrap">
                        {campaign.status === 'pending_approval' && (
                          <Button size="sm" variant="outline" onClick={() => handlePause(campaign.id)}>
                            <Pause className="h-4 w-4 mr-1" />
                            Pause
                          </Button>
                        )}
                        
                        {(campaign.status === 'draft' || campaign.status === 'rejected') && (
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(campaign.id)}>
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        )}

                        <Button size="sm" variant="outline">
                          <Copy className="h-4 w-4 mr-1" />
                          Duplicate
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </FocusedLayout>
  );
};
